/**
 * Dimensões de filtro do Cadastro de Suprimentos (padrão Excel) + filtros
 * rápidos do redesign (KPIs e chips).
 *
 * Cada dimensão sabe extrair o VALOR de um produto; as opções (com
 * contagem) nascem da própria lista carregada, como no Excel. A seleção é
 * `null` (sem filtro) ou `Set` de valores — contrato do `FiltroExcel`.
 *
 * O filtro rápido (`rapido`) é UM só por vez e compõe com as dimensões:
 * clicar no KPI "Abaixo do mínimo" e depois abrir Filtros → Tipo = MDF
 * mostra só MDF abaixo do mínimo. KPI e chip apontam para o mesmo id, então
 * o estado é um só e nunca há duas verdades na tela. "Sem fornecedor" e
 * "sem giro" lêem campos servidos pelo backend (ADR-0070).
 */
import { fmtMm, labelCategoria, labelTipo } from "./colunas";
import { semFornecedor, semGiro, semPreco, temExcesso } from "./derivados";

const VAZIO = "—";

export const DIMENSOES = [
  { key: "tipo", rotulo: "Tipo", valor: (p, ctx) => labelTipo(p, ctx.tipos) },
  { key: "categoria", rotulo: "Categoria", valor: (p) => labelCategoria(p.categoria) },
  { key: "unidade", rotulo: "Unidade", valor: (p) => p.unidade || VAZIO },
  { key: "cor", rotulo: "Cor", valor: (p) => (p.atributos || {}).cor || VAZIO },
  { key: "fabricante", rotulo: "Fabricante", valor: (p) => p.fabricante || VAZIO },
  { key: "espessura", rotulo: "Espessura", valor: (p) => fmtMm(p.espessura_mm) },
  { key: "situacao", rotulo: "Situação", valor: (p) => (p.ativo ? "Ativo" : "Inativo") },
  {
    key: "estoque", rotulo: "Estoque",
    valor: (p) => (Number(p.saldo) < 0 ? "Saldo negativo" : p.alerta_minimo ? "Abaixo do mínimo" : "OK"),
  },
];

export const SELECAO_VAZIA = Object.fromEntries(DIMENSOES.map((d) => [d.key, null]));

/** Filtros rápidos. `passa(p, ctx)` decide; `ctx.tipos` é o catálogo de tipos. */
export const RAPIDOS = [
  { id: "ativos", label: "Total ativos", tone: "neutral", passa: (p) => p.ativo },
  { id: "abaixo", label: "Abaixo do mínimo", tone: "danger", passa: (p) => p.ativo && Boolean(p.alerta_minimo) },
  { id: "sem_preco", label: "Sem preço", tone: "warning", passa: (p) => semPreco(p) },
  { id: "sem_fornecedor", label: "Sem fornecedor", tone: "neutral", passa: (p) => p.ativo && semFornecedor(p) },
  { id: "excesso", label: "Excesso", tone: "teal", passa: (p) => p.ativo && temExcesso(p) },
  { id: "sem_giro", label: "Sem giro 90 d", tone: "teal", passa: (p) => p.ativo && semGiro(p) },
];

export const RAPIDO_POR_ID = Object.fromEntries(RAPIDOS.map((r) => [r.id, r]));

/** Opções de uma dimensão a partir da lista, com contagem, em ordem alfabética. */
export function opcoesDe(produtos, dimensao, ctx) {
  const contagem = new Map();
  produtos.forEach((p) => {
    const v = dimensao.valor(p, ctx);
    contagem.set(v, (contagem.get(v) || 0) + 1);
  });
  return [...contagem.entries()]
    .sort((a, b) => String(a[0]).localeCompare(String(b[0]), "pt-BR", { numeric: true }))
    .map(([value, count]) => ({ value, label: value, count }));
}

function normalizar(s) {
  return String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

function passaNaBusca(p, termo) {
  if (!termo) return true;
  const alvo = normalizar([
    p.codigo, p.nome, p.descricao, (p.atributos || {}).cor, p.fabricante, p.referencia_fabricante, p.ncm,
  ].filter(Boolean).join(" "));
  return alvo.includes(termo);
}

function passaNasDimensoes(p, selecao, ctx) {
  return DIMENSOES.every((d) => {
    const sel = selecao[d.key];
    if (sel === null || sel === undefined) return true;
    return sel.has(d.valor(p, ctx));
  });
}

/**
 * Busca textual + dimensões + filtro rápido, como o Excel: a linha passa se
 * passa em todos. A busca cobre código, nome, descrição, cor, fabricante,
 * referência e NCM.
 */
export function aplicarFiltros(produtos, { busca, selecao, rapido = null }, ctx) {
  const termo = normalizar(busca).trim();
  const r = rapido ? RAPIDO_POR_ID[rapido] : null;
  return produtos.filter((p) => (
    passaNaBusca(p, termo) && passaNasDimensoes(p, selecao, ctx) && (!r || r.passa(p, ctx))
  ));
}

/** Quantos passam em cada filtro rápido, dado o recorte das dimensões (sem o rápido). */
export function contagensRapidas(produtos, ctx) {
  const c = {};
  RAPIDOS.forEach((r) => { c[r.id] = 0; });
  produtos.forEach((p) => {
    RAPIDOS.forEach((r) => { if (r.passa(p, ctx)) c[r.id] += 1; });
  });
  return c;
}

export function contarDimensoesAtivas(selecao) {
  return DIMENSOES.filter((d) => selecao[d.key]).length;
}

export function resumoChip(set, max = 3) {
  const lista = [...set];
  const cabeca = lista.slice(0, max).join(", ");
  return lista.length > max ? `${cabeca} +${lista.length - max}` : cabeca || "nenhum";
}
